import { array, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useMemo } from 'react';
import { CustomerClassifications, CustomerProfileClassifications } from '@mews-commander/data-access';
import { omitFalsyValues } from '@mews/fp';
import { createHandler } from '@mews/std';
import { IconName } from '@optimus-web/icons';

const classificationsMap: Record<keyof CustomerProfileClassifications, CustomerClassifications> = {
    paymasterAccount: CustomerClassifications.PaymasterAccount,
    blacklist: CustomerClassifications.Blacklist,
    media: CustomerClassifications.Media,
    loyaltyProgram: CustomerClassifications.LoyaltyProgram,
    previousComplaint: CustomerClassifications.PreviousComplaint,
    returning: CustomerClassifications.Returning,
    staff: CustomerClassifications.Staff,
    friendOrFamily: CustomerClassifications.FriendOrFamily,
    topManagement: CustomerClassifications.TopManagement,
    important: CustomerClassifications.Important,
    veryImportant: CustomerClassifications.VeryImportant,
    problematic: CustomerClassifications.Problematic,
    cashlist: CustomerClassifications.Cashlist,
    disabledPerson: CustomerClassifications.DisabledPerson,
    military: CustomerClassifications.Military,
    airline: CustomerClassifications.Airline,
    healthCompliant: CustomerClassifications.HealthCompliant,
    inRoom: CustomerClassifications.InRoom,
    waitingForRoom: CustomerClassifications.WaitingForRoom,
    student: CustomerClassifications.Student,
};

const mapFlagsToArray =
    <T extends string>(flagToEnumMap: Record<T, CustomerClassifications>, preserveOrder?: boolean) =>
    (booleanMap: Record<T, boolean>): Array<CustomerClassifications> =>
        preserveOrder
            ? // on guest profile header, we want to keep the same order that comes form the backend
              Object.entries(booleanMap).reduce((acc: Array<CustomerClassifications>, [key, hasClassification]) => {
                  const classification = flagToEnumMap[key as T];
                  return hasClassification ? [...acc, classification] : acc;
              }, [])
            : pipe(
                  booleanMap,
                  omitFalsyValues,
                  record.keys<T>,
                  array.map((checkFlagName: T) => flagToEnumMap[checkFlagName])
              );

const guestClassificationIconNameHandler = createHandler<CustomerClassifications, IconName | undefined>(
    {
        [CustomerClassifications.PaymasterAccount]: IconName.Paymaster,
        [CustomerClassifications.Blacklist]: IconName.Blacklist,
        [CustomerClassifications.Media]: IconName.Media,
        [CustomerClassifications.LoyaltyProgram]: IconName.Loyalty,
        [CustomerClassifications.PreviousComplaint]: IconName.Complaint,
        [CustomerClassifications.Returning]: IconName.Restore,
        [CustomerClassifications.Staff]: IconName.Staff,
        [CustomerClassifications.FriendOrFamily]: IconName.FriendFamily,
        [CustomerClassifications.TopManagement]: IconName.TopManagement,
        [CustomerClassifications.Important]: IconName.Important,
        [CustomerClassifications.VeryImportant]: IconName.VeryImportant,
        [CustomerClassifications.Problematic]: IconName.Problematic,
        [CustomerClassifications.Cashlist]: IconName.Cashlist,
        [CustomerClassifications.DisabledPerson]: IconName.Disability,
        [CustomerClassifications.InRoom]: IconName.HouseUse,
        [CustomerClassifications.WaitingForRoom]: IconName.UserClock,
        [CustomerClassifications.Military]: IconName.Military,
        [CustomerClassifications.Airline]: IconName.Plane,
        [CustomerClassifications.HealthCompliant]: IconName.CovidProtection,
        [CustomerClassifications.Student]: IconName.Education,
    },
    undefined
);

export interface useGuestClassificationsSplitParams {
    guestProfileClassifications: CustomerProfileClassifications;
    preserveOrder?: boolean;
}

export const useGuestClassificationsSplit = ({
    guestProfileClassifications,
    preserveOrder = false,
}: useGuestClassificationsSplitParams) =>
    useMemo(() => {
        const classificationsArray = pipe(
            mapFlagsToArray(classificationsMap, preserveOrder)(guestProfileClassifications),
            array.map((key) => ({
                enumValue: key,
                iconName: guestClassificationIconNameHandler(key),
            }))
        );

        const [first, second, ...rest] = classificationsArray;

        return { first, second, rest };
    }, [guestProfileClassifications, preserveOrder]);
